import React, { useEffect, useMemo, useState } from 'react';
import { Search, Filter, Calendar, Clock, Tags, ChevronDown, X, Loader2 } from 'lucide-react';
import SearchableSelect from '@/client/components/admin/SearchableSelect';
import CategoryFilterDrawer from '@/client/components/admin/ListingManagement/CategoryFilterDrawer';
import TagFilterDrawer from '@/client/components/admin/ListingManagement/TagFilterDrawer';
import { useClientStore } from '@/client/stores/client';
import type { Category } from '@/client/types/entity-full';
import { FilterStatus, Period } from '@/client/utils/filters';

/** Flatten the category tree into an etid → label map for the filter button. */
const collectLabels = (nodes: Category[] | undefined, map: Map<number, string>) => {
  (nodes ?? []).forEach((t) => {
    if (t.etid != null) {
      map.set(t.etid, t.name_friendly || t.name_menu || t.name_system || `Category #${t.etid}`);
    }
    if (t.children?.length) collectLabels(t.children, map);
  });
  return map;
};

const periodOptions = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7days', label: 'Last 7 Days' },
  { value: 'last30days', label: 'Last 30 Days' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'thisYear', label: 'This Year' },
  { value: 'lastYear', label: 'Last Year' },
  { value: 'custom', label: 'Custom Range' }
];

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'published', label: 'Published' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

/** Comma-separated tagid string ⇄ number[] the TagPicker speaks. */
const parseTagIds = (csv?: string): number[] =>
  (csv ?? '')
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);

const serializeTagIds = (ids: number[]): string => ids.join(',');

interface ListingFiltersProps {
  onSearch: (query: string) => void;
  searchValue?: string;
  onFilterChange: (key: string, value: string) => void;
  filters: {
    type: string;
    period: Period;
    status: FilterStatus;
    tagids?: string;
    tag_match?: string;
    date_from?: string;
    date_to?: string;
  };
  /** True while the listing query is fetching (initial load or filter refetch). */
  loading?: boolean;
}

const ListingFilters: React.FC<ListingFiltersProps> = ({
  onSearch,
  searchValue,
  onFilterChange,
  filters,
  loading
}) => {
  const [tagsDrawerOpen, setTagsDrawerOpen] = useState(false);
  const [typeDrawerOpen, setTypeDrawerOpen] = useState(false);
  const selectedTagIds = parseTagIds(filters.tagids);
  const tagMatch = filters.tag_match === 'all' ? 'all' : 'any';

  // Category tree (shared client-store copy) — used to label the selected
  // category on the Type button. The drawer loads/uses the same store.
  const categories = useClientStore((s) => s.categories);
  const fetchCategories = useClientStore((s) => s.fetchCategories);
  useEffect(() => {
    if (filters.type && !categories) fetchCategories();
  }, [filters.type, categories, fetchCategories]);
  const typeLabels = useMemo(() => collectLabels(categories ?? undefined, new Map<number, string>()), [categories]);
  const selectedTypeLabel =
    filters.type && typeLabels.get(Number(filters.type)) ? typeLabels.get(Number(filters.type))! : 'All Types';

  return (
    <div className="bg-white p-4 rounded-lg mb-6 border border-gray-200">
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex items-center gap-4 w-full md:w-auto flex-wrap">
          {/* Search Input */}
          <div className="relative w-full md:w-64">
            <input
              type="text"
              value={searchValue}
              placeholder="Search entities..."
              className="w-full pl-10 pr-10 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onChange={(e) => onSearch(e.target.value)}
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            {loading && (
              <Loader2
                className="absolute right-3 top-2.5 h-5 w-5 animate-spin text-gray-400"
                aria-label="Loading"
              />
            )}
          </div>

          {/* Type / category filter — opens a searchable category tree drawer */}
          <div
            className={`flex h-9 w-full items-center gap-2 rounded-md border px-3 text-sm shadow-sm md:w-48 ${
              filters.type
                ? 'border-blue-300 bg-blue-50 text-blue-700'
                : 'border-input bg-background text-gray-600'
            }`}
          >
            <button
              type="button"
              onClick={() => setTypeDrawerOpen(true)}
              className="flex min-w-0 flex-1 items-center gap-2 text-left"
              title={selectedTypeLabel}
            >
              <Filter className="h-4 w-4 shrink-0 text-gray-400" />
              <span className="min-w-0 flex-1 truncate">{selectedTypeLabel}</span>
            </button>
            {loading ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" />
            ) : filters.type ? (
              <button
                type="button"
                onClick={() => onFilterChange('type', '')}
                className="shrink-0 rounded p-0.5 text-blue-500 hover:bg-blue-100 hover:text-blue-700"
                aria-label="Clear category filter"
                title="Clear category"
              >
                <X className="h-4 w-4" />
              </button>
            ) : (
              <ChevronDown
                className="h-4 w-4 shrink-0 cursor-pointer text-gray-400"
                onClick={() => setTypeDrawerOpen(true)}
              />
            )}
          </div>

          {/* Period Filter */}
          <SearchableSelect
            options={periodOptions}
            value={filters.period}
            onChange={(value) => onFilterChange('period', value)}
            placeholder="Select Period"
            icon={Calendar}
            className="w-full md:w-48"
            loading={loading}
          />

          {/* Custom date range — only when "Custom Range" is picked */}
          {filters.period === 'custom' && (
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 text-xs text-gray-500">
                From
                <input
                  type="date"
                  value={filters.date_from ?? ''}
                  max={filters.date_to || undefined}
                  onChange={(e) => onFilterChange('date_from', e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm shadow-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </label>
              <label className="flex items-center gap-1 text-xs text-gray-500">
                To
                <input
                  type="date"
                  value={filters.date_to ?? ''}
                  min={filters.date_from || undefined}
                  onChange={(e) => onFilterChange('date_to', e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm shadow-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </label>
              {(filters.date_from || filters.date_to) && (
                <button
                  type="button"
                  onClick={() => {
                    onFilterChange('date_from', '');
                    onFilterChange('date_to', '');
                  }}
                  className="rounded p-1 text-gray-400 hover:text-gray-700"
                  aria-label="Clear dates"
                  title="Clear dates"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}

          {/* Status Filter */}
          <SearchableSelect
            options={statusOptions}
            value={filters.status}
            onChange={(value) => onFilterChange('status', value)}
            placeholder="Select Status"
            icon={Clock}
            className="w-full md:w-48"
            loading={loading}
          />

          {/* Tag filter — opens a searchable tag drawer */}
          <button
            type="button"
            onClick={() => setTagsDrawerOpen(true)}
            className={`inline-flex h-9 items-center gap-2 rounded-md border px-3 text-sm transition-colors ${
              selectedTagIds.length > 0
                ? 'border-blue-300 bg-blue-50 text-blue-700'
                : 'border-input bg-background text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Tags className="h-4 w-4 text-gray-400" />
            {selectedTagIds.length > 0 ? `Tags · ${selectedTagIds.length}` : 'Tags'}
            <ChevronDown className="h-4 w-4 text-gray-400" />
          </button>
        </div>
      </div>

      <CategoryFilterDrawer
        open={typeDrawerOpen}
        onClose={() => setTypeDrawerOpen(false)}
        value={filters.type}
        onChange={(etid) => onFilterChange('type', etid)}
      />

      <TagFilterDrawer
        open={tagsDrawerOpen}
        onClose={() => setTagsDrawerOpen(false)}
        value={selectedTagIds}
        onChange={(ids) => onFilterChange('tagids', serializeTagIds(ids))}
        match={tagMatch}
        onMatchChange={(mode) => onFilterChange('tag_match', mode)}
      />
    </div>
  );
};

export default ListingFilters;
